import { Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { CapsuleTrend } from '@/components';
import { GraphFact } from '@/components/Charts/types';
import { kgToTonneFormatter } from '@/components/Charts/utils';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { getBenchmarksFromFacts, useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';

export type AboveCarbonCardStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<GraphFact[] | null> | undefined;
    moderate: PlotReportFactElement<GraphFact[] | null> | undefined;
    low: PlotReportFactElement<GraphFact[] | null> | undefined;
  };
};

export const AboveCarbonCardStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  oneYearPctChangeFact,
  benchmarkFact,
}: AboveCarbonCardStatsTileProps) => {
  const { t } = useTranslation();

  const carbonAgLatestAnalysis = formatFactKilogramsToTonnes(latestAnalysisFact);

  const cagBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
    formatter: kgToTonneFormatter,
  });

  const benchmarkType = useBenchmarkForFact(cagBenchmarks, carbonAgLatestAnalysis?.value);
  assertBenchmarkType(benchmarkType, 'AboveCarbonCardStatsTile');
  assertBenchmarkType(benchmarkType, 'AboveCarbonCardStatsTile', BenchmarkType.unchanged);
  assertBenchmarkType(benchmarkType, 'AboveCarbonCardStatsTile', BenchmarkType.stable);
  assertBenchmarkType(benchmarkType, 'AboveCarbonCardStatsTile', BenchmarkType.alerted);

  const carbonAg1yrChange = formatFactKilogramsToTonnes(oneYearUpliftFact);

  let carbonStorageAgTrendDisplay = `${useDisplayNumber(oneYearPctChangeFact?.value ?? 0, {
    signDisplay: 'always',
  })} %`;

  carbonStorageAgTrendDisplay = !isNullish(oneYearPctChangeFact?.value) ? carbonStorageAgTrendDisplay : '-';

  const lastMeasuredDate = printLatestDate([carbonAgLatestAnalysis?.measured_at]);
  const carbonAg1yrChangeInitialMeasuredAt = lastMeasuredDate;
  const carbonAg1yrChangePreviousMeasuredAt = printNthYearDate([carbonAg1yrChange?.measured_at], -1);

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='carbon-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage')}
          supplementaryInfo={`${t('shared.ncaDetail.details.labels.updated')} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.title'),
            body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.body'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-6'>
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.latestAnalysis')}
            fact={carbonAgLatestAnalysis}
            trend={
              typeof oneYearPctChangeFact?.value === 'number' && (
                <CapsuleTrend variant={oneYearPctChangeFact.value < 0 ? 'negative' : 'positive'}>
                  {carbonStorageAgTrendDisplay}
                </CapsuleTrend>
              )
            }
            capsuleIconProps={{
              icon: getNCChangeIcon(carbonAgLatestAnalysis),
              variant: getNCChangeIconVariant(carbonAgLatestAnalysis),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
              body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
              popoverTriggerProps: {
                className: 'text-xs',
              },
            }}
          />
          {!isNullish(carbonAg1yrChange?.value) && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.oneYearChange')}
              fact={carbonAg1yrChange}
              capsuleIconProps={{
                icon: getNCChangeIcon(carbonAg1yrChange),
                variant: getNCChangeIconVariant(carbonAg1yrChange),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.oneYearChange.title'),
                body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.oneYearChange.body', {
                  date: carbonAg1yrChangeInitialMeasuredAt,
                  previousDate: carbonAg1yrChangePreviousMeasuredAt,
                }),
                popoverTriggerProps: {
                  'data-testid': 'cag-since-previous-analysis-tooltip',
                },
              }}
            />
          )}
        </Stack>
        {benchmarkType && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <BenchmarkPill benchmarkType={benchmarkType} />
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.aboveGroundCarbon.explainers.benchmark.title')}
            </span>
            <Trans
              i18nKey={`shared.ncaDetail.details.aboveGroundCarbon.explainers.benchmark.${benchmarkType}`}
              components={{
                p: <p className='typography-body1 text-text-secondary' />,
              }}
            />
          </Stack>
        )}
      </div>
    </Stack>
  );
};
